// ✅ src/pages/VehicleV2/VehicleDailyProgressListing.jsx  (UPDATED FULL FILE)
// Backend expected:
//   app.use("/api/dailyProgressListing", dailyProgressListing);
//
// APIs used:
//   GET  http://nissi-india.com/api/dailyProgressListing/init?branchId=&loginUserId=&branchType=
//   GET  http://nissi-india.com/api/dailyProgressListing/list?branchId=&loginUserId=&branchType=&vehicleType=O|H&storeId=&vehicleBasisId=&vehicleSubTypeId=&vehicleCode=&vehicleName=&hiredFrom=&page=1&limit=10
//   POST http://nissi-india.com/api/dailyProgressListing/delete   body:{ ids:[...], loginUserId, branchId, branchType }
//
// ✅ Updated pattern like JobCardEntry / BatteryTesting / latest screens:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - useMemo + useCallback pattern
// - loading + pageLoading + deleting states
// - safe mapping for init/list response
// - pagination support
// - same UI structure style
// - view action opens entry screen with vehicleId
// - delete API wired safely (if backend exists)

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaFilter, FaSearch, FaTrash, FaEye, FaUndo } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/dailyProgressListing";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const VehicleDailyProgressListing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const queryVehicleType = useMemo(() => {
    const sp = new URLSearchParams(location?.search || "");
    return clean(sp.get("vehicleType") || sp.get("type") || "O").toUpperCase();
  }, [location]);

  const [vehicleType, setVehicleType] = useState(
    queryVehicleType === "H" ? "H" : "O"
  );

  const [showFilters, setShowFilters] = useState(true);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [stores, setStores] = useState([]);
  const [vehicleBasisList, setVehicleBasisList] = useState([]);
  const [vehicleSubTypes, setVehicleSubTypes] = useState([]);
  const [hiredFromList, setHiredFromList] = useState([]);

  const [filters, setFilters] = useState({
    storeId: "Select",
    hiredFrom: "Select",
    vehicleBasisId: "Select",
    vehicleSubTypeId: "Select",
    vehicleCode: "",
    vehicleName: "",
  });

  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  const canSelectAll = useMemo(() => rows.length > 0, [rows.length]);

  const selectAll = useMemo(() => {
    return rows.length > 0 && selectedIds.length === rows.length;
  }, [rows, selectedIds]);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const cardClass = `p-6 mt-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : `bg-white ${currentSkin.glass} text-black`
  }`;

  const mapDropdownItem = useCallback((item) => {
    const raw = item || {};
    return {
      id: toText(
        raw?.id ??
          raw?.value ??
          raw?.fld_storeid ??
          raw?.fld_vehicle_basisid ??
          raw?.fld_vehicletypeindex ??
          raw?.fld_hiredfromid ??
          raw?.fld_id
      ),
      name: toText(
        raw?.name ??
          raw?.text ??
          raw?.label ??
          raw?.fld_storename ??
          raw?.fld_vehicle_basistype ??
          raw?.fld_vehiclebasis ??
          raw?.fld_vehicletypename ??
          raw?.fld_hiredfrom ??
          raw?.fld_name
      ),
      _raw: raw,
    };
  }, []);

  const mapRow = useCallback((r) => {
    const raw = r || {};
    return {
      id: toInt(
        raw?.id ??
          raw?.vehicleId ??
          raw?.fld_vehicleid ??
          raw?.mainId ??
          raw?.fld_id ??
          0
      ),
      mainType: toText(
        raw?.mainType ??
          raw?.type ??
          raw?.vehicleMainType ??
          raw?.fld_vehiclemaintype
      ),
      code: toText(raw?.code ?? raw?.vehicleCode ?? raw?.fld_vehicle_code),
      regNo: toText(
        raw?.regNo ?? raw?.vehicleRegNo ?? raw?.fld_vehicle_regno
      ),
      name: toText(raw?.name ?? raw?.vehicleName ?? raw?.fld_vehicle_name),
      basis: toText(
        raw?.basis ?? raw?.vehicleBasis ?? raw?.fld_vehicle_basistype
      ),
      hiredFrom: toText(
        raw?.hiredFrom ?? raw?.supplierName ?? raw?.fld_hiredfrom
      ),
      _raw: raw,
    };
  }, []);

  const appliedFilters = useMemo(
    () => ({
      branchId: toInt(branchId, 0),
      loginUserId: toInt(loginUserId, 0),
      branchType: clean(branchType),
      vehicleType: clean(vehicleType || "O"),
      storeId: clean(filters.storeId || "Select"),
      vehicleBasisId: clean(filters.vehicleBasisId || "Select"),
      vehicleSubTypeId: clean(filters.vehicleSubTypeId || "Select"),
      vehicleCode: clean(filters.vehicleCode),
      vehicleName: clean(filters.vehicleName),
      hiredFrom:
        clean(vehicleType) === "H"
          ? clean(filters.hiredFrom || "Select")
          : "Select",
    }),
    [branchId, loginUserId, branchType, vehicleType, filters]
  );

  const paginationText = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(total, page * limit);
    return `${from}-${to} of ${total}`;
  }, [page, limit, total]);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: toInt(branchId, 0),
          loginUserId: toInt(loginUserId, 0),
          branchType: clean(branchType),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      const dd = data?.dropdowns || data || {};

      const safeStores = Array.isArray(dd?.stores)
        ? dd.stores.map(mapDropdownItem)
        : [];
      const safeBasis = Array.isArray(dd?.vehicleBasis)
        ? dd.vehicleBasis.map(mapDropdownItem)
        : [];
      const safeSubTypes = Array.isArray(dd?.vehicleSubTypes)
        ? dd.vehicleSubTypes.map(mapDropdownItem)
        : [];
      const safeHiredFrom = Array.isArray(dd?.hiredFrom)
        ? dd.hiredFrom.map(mapDropdownItem)
        : [];

      setStores(safeStores);
      setVehicleBasisList(safeBasis);
      setVehicleSubTypes(safeSubTypes);
      setHiredFromList(safeHiredFrom);

      const defaultStoreId = toText(dd?.defaultStoreId, "Select") || "Select";

      setFilters((prev) => ({
        ...prev,
        storeId: defaultStoreId,
      }));
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Init failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    branchId,
    loginUserId,
    branchType,
    commonHeaders,
    mapDropdownItem,
  ]);

  const loadList = useCallback(
    async (nextPage = page, nextLimit = limit, overrideParams = {}) => {
      try {
        setLoading(true);

        const params = {
          ...appliedFilters,
          page: toInt(nextPage, 1),
          limit: toInt(nextLimit, 10),
          ...overrideParams,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        const list = Array.isArray(data?.list) ? data.list.map(mapRow) : [];
        setRows(list);
        setSelectedIds([]);
        setTotal(toInt(data?.total ?? list.length, 0));
        setPage(toInt(data?.page, nextPage));
        setLimit(toInt(data?.limit, nextLimit));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "List failed"
        );
        setRows([]);
        setSelectedIds([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, appliedFilters, commonHeaders, mapRow]
  );

  const handleSelectAll = () => {
    if (!canSelectAll) return;

    if (selectAll) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(rows.map((r) => r.id));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setPage(1);
    loadList(1, limit);
  };

  const handleReset = () => {
    const defaultStoreId =
      stores.length > 0
        ? toText(
            stores.find((x) => clean(x.id) !== "Select")?.id ??
              filters.storeId ??
              "Select"
          )
        : "Select";

    setFilters({
      storeId: defaultStoreId || "Select",
      hiredFrom: "Select",
      vehicleBasisId: "Select",
      vehicleSubTypeId: "Select",
      vehicleCode: "",
      vehicleName: "",
    });
    setSelectedIds([]);
  };

  const handleViewAll = () => {
    const newVehicleType = "O";

    setVehicleType(newVehicleType);
    setPage(1);

    const defaultStoreId = toText(
      stores.find((x) => clean(x.id) !== "Select")?.id || filters.storeId || "Select"
    );

    const cleared = {
      storeId: defaultStoreId || "Select",
      hiredFrom: "Select",
      vehicleBasisId: "Select",
      vehicleSubTypeId: "Select",
      vehicleCode: "",
      vehicleName: "",
    };

    setFilters(cleared);

    loadList(1, limit, {
      vehicleType: newVehicleType,
      storeId: cleared.storeId,
      hiredFrom: "Select",
      vehicleBasisId: "Select",
      vehicleSubTypeId: "Select",
      vehicleCode: "",
      vehicleName: "",
    });
  };

  const handleView = (row) => {
    const vid = toInt(row?.id, 0);
    if (!vid) {
      toast.warn("Vehicle Id not found");
      return;
    }

    window.open(
      `/vehicle/VehicleDailyProgressEntrySLT?vehicleId=${vid}`,
      "_blank"
    );
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one record");
      return;
    }

    try {
      setDeleting(true);

      const body = {
        ids: selectedIds,
        loginUserId: toInt(loginUserId, 0),
        branchId: toInt(branchId, 0),
        branchType: clean(branchType),
      };

      const { data } = await axios.post(`${API_BASE}/delete`, body, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(
        data?.message || `${selectedIds.length} record(s) deleted successfully`
      );

      setSelectedIds([]);
      await loadList(page, limit);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Delete API not available / Delete failed"
      );
    } finally {
      setDeleting(false);
    }
  };

  const canPrev = page > 1;
  const canNext = page < totalPages;

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    loadList(page, limit);
  }, [page, limit, vehicleType, loadList]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE DAILY PROGRESS ENTRY
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Daily Progress Entry Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              {(loading || pageLoading || deleting) && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {deleting ? "Deleting..." : "Loading..."}
                </span>
              )}

              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800${
                        showFilters ? " bg-gray-800" : ""
                      }`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50${
                        showFilters ? " bg-gray-50" : ""
                      }`
                }`}
                disabled={loading || pageLoading || deleting}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filter area */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter />
                FILTRATION AREA
              </h3>

              {/* Vehicle type */}
              <div className="mb-6">
                <label className="mr-6">
                  <input
                    type="radio"
                    name="vehicleType"
                    value="O"
                    checked={vehicleType === "O"}
                    onChange={() => {
                      setVehicleType("O");
                      setPage(1);
                      setFilters((prev) => ({
                        ...prev,
                        hiredFrom: "Select",
                      }));
                    }}
                    className="mr-2"
                    disabled={loading || pageLoading || deleting}
                  />
                  OwnVehicle
                </label>

                <label>
                  <input
                    type="radio"
                    name="vehicleType"
                    value="H"
                    checked={vehicleType === "H"}
                    onChange={() => {
                      setVehicleType("H");
                      setPage(1);
                    }}
                    className="mr-2"
                    disabled={loading || pageLoading || deleting}
                  />
                  HireVehicle
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Store */}
                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={filters.storeId}
                    onChange={(e) =>
                      handleFilterChange("storeId", e.target.value)
                    }
                    className={inputClass}
                    disabled
                    title="Disabled (same as ASPX). Enable if needed."
                  >
                    <option value="Select">Select</option>
                    {stores.map((s, idx) => (
                      <option key={`${s.id}-${idx}`} value={toText(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Supplier hired from */}
                <div>
                  <label className={labelClass}>Supplier Hired From</label>
                  <select
                    value={filters.hiredFrom}
                    onChange={(e) =>
                      handleFilterChange("hiredFrom", e.target.value)
                    }
                    className={inputClass}
                    disabled={
                      vehicleType !== "H" || loading || pageLoading || deleting
                    }
                  >
                    <option value="Select">Select</option>
                    {hiredFromList.map((h, idx) => (
                      <option key={`${h.id}-${idx}`} value={toText(h.id)}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle basis */}
                <div>
                  <label className={labelClass}>Vehicle Basis</label>
                  <select
                    value={filters.vehicleBasisId}
                    onChange={(e) =>
                      handleFilterChange("vehicleBasisId", e.target.value)
                    }
                    className={inputClass}
                    disabled={loading || pageLoading || deleting}
                  >
                    <option value="Select">Select</option>
                    {vehicleBasisList.map((b, idx) => (
                      <option key={`${b.id}-${idx}`} value={toText(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle sub type */}
                <div>
                  <label className={labelClass}>Vehicle Sub Type</label>
                  <select
                    value={filters.vehicleSubTypeId}
                    onChange={(e) =>
                      handleFilterChange("vehicleSubTypeId", e.target.value)
                    }
                    className={inputClass}
                    disabled={loading || pageLoading || deleting}
                  >
                    <option value="Select">Select</option>
                    {vehicleSubTypes.map((t, idx) => (
                      <option key={`${t.id}-${idx}`} value={toText(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle code */}
                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) =>
                      handleFilterChange("vehicleCode", e.target.value)
                    }
                    className={inputClass}
                    placeholder="Type vehicle code"
                    disabled={loading || pageLoading || deleting}
                  />
                </div>

                {/* Vehicle name */}
                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) =>
                      handleFilterChange("vehicleName", e.target.value)
                    }
                    className={inputClass}
                    placeholder="Type vehicle name"
                    disabled={loading || pageLoading || deleting}
                  />
                </div>
              </div>

              {/* buttons */}
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleReset}
                  disabled={loading || pageLoading || deleting}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium border transition-all duration-200 ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-700"
                      : "border-gray-300 text-gray-800 hover:bg-gray-100"
                  } ${
                    loading || pageLoading || deleting
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <FaUndo />
                  Clear All Filters
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={handleSearch}
                    disabled={loading || pageLoading || deleting}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading || pageLoading || deleting
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <FaSearch />
                    Search
                  </button>

                  <button
                    onClick={handleViewAll}
                    disabled={loading || pageLoading || deleting}
                    className={`flex items-center gap-2 px-6 py-2 border rounded-lg font-semibold transition-colors ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    } ${
                      loading || pageLoading || deleting
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    View All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Grid/table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-[1400px] w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Vehicle Main Type
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Vehicle Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Vehicle Registration Number
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Vehicle Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Vehicle Basis
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Hired From
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Actions
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={
                            !canSelectAll ||
                            loading ||
                            pageLoading ||
                            deleting
                          }
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading || pageLoading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-10">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-10">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.id || idx}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.mainType}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-blue-400" : "text-blue-600"
                          }`}
                        >
                          {r.code}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.regNo}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.name}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.basis}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.hiredFrom}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleView(r)}
                            disabled={loading || pageLoading || deleting}
                            className={`inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg ${
                              loading || pageLoading || deleting
                                ? "opacity-70 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            <FaEye />
                            View
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelect(r.id)}
                            disabled={loading || pageLoading || deleting}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination bar */}
            <div
              className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-t ${
                isDark
                  ? "border-gray-700 bg-gray-900"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {paginationText}
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  disabled={loading || pageLoading || deleting}
                  className={inputClass}
                  style={{ width: 120 }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>

                <button
                  disabled={!canPrev || loading || pageLoading || deleting}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-4 py-2 rounded-lg border ${
                    canPrev && !loading && !pageLoading && !deleting
                      ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "opacity-50 cursor-not-allowed"
                  } ${
                    isDark
                      ? "border-gray-700 text-gray-200"
                      : "border-gray-300 text-gray-700"
                  }`}
                >
                  Prev
                </button>

                <div
                  className={`text-sm px-2 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Page {page} / {totalPages}
                </div>

                <button
                  disabled={!canNext || loading || pageLoading || deleting}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={`px-4 py-2 rounded-lg border ${
                    canNext && !loading && !pageLoading && !deleting
                      ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "opacity-50 cursor-not-allowed"
                  } ${
                    isDark
                      ? "border-gray-700 text-gray-200"
                      : "border-gray-300 text-gray-700"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {/* Delete button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={handleDelete}
              disabled={
                selectedIds.length === 0 || loading || pageLoading || deleting
              }
              className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                selectedIds.length === 0 || loading || pageLoading || deleting
                  ? "bg-gray-400 cursor-not-allowed"
                  : `${buttonClass} ${hoverClass}`
              }`}
            >
              <FaTrash />
              {deleting ? "Deleting..." : "Delete Selected"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleDailyProgressListing;